import React, { useEffect, useState, Suspense } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  where,
  limit,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAsyncResource } from "hooks/useAsyncResource";
import {
  defaultStartingElo,
  fetchChampions,
  appVersion,
} from "constants/gameDefaults";
import {
  Game,
  GameModeEnum,
  GameStateEnum,
  GameStatusEnum,
} from "interfaces/game";
import { setGame } from "stores/game/GameSlice";
import { printableNumber } from "utils/numbers";
import { calculateElo } from "utils/elo";

const containerVariants = {
  start: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  start: { opacity: 0, x: "-100%" },
  animate: { opacity: 1, x: 0 },
};
type FetchedChampion = {
  id: string;
  key: string;
  icon: string;
  splash: string;
};

type GameHistoryProps = {
  userId: string;
  onStatsLoaded?: (stats: { wins: number; total: number }) => void;
};

async function fetchGameComponent(
  game: Game,
  userUId: string,
): Promise<{ opponentUsername: string; opponentLP: number }> {
  const tmpDb = getFirestore();
  const userSide = game?.players.findIndex((player) => player === userUId);
  const opponentLP = userSide === 0 ? game.player2Elo : game.player1Elo;
  const opponentId = game?.players[userSide === 0 ? 1 : 0];

  const opponentDoc = await getDoc(doc(tmpDb, "users", opponentId));
  let opponentUsername = "unknown";
  if (opponentDoc.exists()) {
    // eslint-disable-next-line fp/no-mutation
    opponentUsername = opponentDoc.data().username || "unknown";
  }

  return { opponentUsername, opponentLP: opponentLP || 1000 };
}

function GameComponent({
  game,
  championsList,
  userUid,
  fetchGameComponentFunction,
}: {
  game: Game;
  championsList: Map<string, FetchedChampion>;
  userUid: string;
  fetchGameComponentFunction: (
    game: Game,
    userUid: string,
  ) => Promise<{ opponentUsername: string; opponentLP: number }>;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const data = useAsyncResource(fetchGameComponentFunction, game, userUid);
  const userSide = game?.players.findIndex((player) => player === userUid);

  const playerIsWinner = game.winner?.userId === userUid;
  const isUserPlayer1 = userSide === 0;
  const isPlayer1Winner = game.winner?.userId === game.players[0];
  const playerChampions = isUserPlayer1
    ? game.bluePickedChampions
    : game.redPickedChampions;
  const opponentChampions = isUserPlayer1
    ? game.redPickedChampions
    : game.bluePickedChampions;

  function getElosFromGame(gameGetElo: Game): {
    winnerElo: number;
    loserElo: number;
  } {
    const player1Elo = gameGetElo.player1Elo || defaultStartingElo;
    const player2Elo = gameGetElo.player2Elo || defaultStartingElo;

    const winnerElo = isPlayer1Winner ? player1Elo : player2Elo;
    const loserElo = isPlayer1Winner ? player2Elo : player1Elo;

    return { winnerElo, loserElo };
  }

  const { winnerElo, loserElo } = getElosFromGame(game);
  const { eloGain } = calculateElo(winnerElo, loserElo);

  const handleOpenInLab = () => {
    const labGame = {
      id: null,
      mode: GameModeEnum.SIMULATION,
      players: ["player1", "player2"],
      state: GameStateEnum.FINISHED,
      status: GameStatusEnum.RESULTS,
      currentTurn: 0,
      bluePickedChampions: game.bluePickedChampions.map((c) => ({
        championId: c.championId,
        userId: "player1",
      })),
      redPickedChampions: game.redPickedChampions.map((c) => ({
        championId: c.championId,
        userId: "player2",
      })),
      blueBannedChampions: [],
      redBannedChampions: [],
      rolesLockedIn: [],
      teams: [],
      winner: null,
      loser: null,
      date: new Date().toISOString(),
      version: `${appVersion}`,
      lobbyId: null,
      timestamp: null,
    };
    dispatch(setGame(labGame));
    navigate("/game", { state: { startInEditMode: true } });
  };

  // no champions for this game, it was probably cancelled
  if (!playerChampions) {
    return null;
  }

  return (
    <div className="gameHistoryWrapper" key={game.id}>
      <div className="gameInfosContainer">
        <div
          className="gameWinLooseContainer"
          style={{ color: playerIsWinner ? "green" : "red" }}
        >
          <p>{game.winner?.userId === userUid ? "VICTORY" : "DEFEAT"}</p>
          {game.mode === GameModeEnum.COMPETITIVE ? (
            <p>{printableNumber(playerIsWinner ? eloGain : -eloGain)} ELO</p>
          ) : null}
        </div>
        <div className="gameInfosWrapper">
          <span>{new Date(game.date).toLocaleString()}</span>
          <span>
            {data?.opponentUsername.substring(0, 12)} -
            <p>
              {game.mode === GameModeEnum.COMPETITIVE
                ? `${data?.opponentLP} LP`
                : game.mode}
            </p>
          </span>
        </div>
      </div>
      <div className="championsSection">
        <motion.div
          className="championsWrapper playerChampions"
          variants={containerVariants}
          initial="start"
          animate="animate"
        >
          {playerChampions?.map((champion) => {
            const championData = championsList.get(champion.championId);
            if (!championData) {
              return null;
            }
            return (
              <motion.img
                key={champion.championId}
                src={championData.icon}
                alt={champion.championId}
                variants={itemVariants}
              />
            );
          })}
        </motion.div>
        {opponentChampions && (
          <motion.div
            className="championsWrapper opponentChampions"
            variants={containerVariants}
            initial="start"
            animate="animate"
          >
            {opponentChampions.map((champion) => {
              const championData = championsList.get(champion.championId);
              if (!championData) {
                return null;
              }
              return (
                <motion.img
                  key={champion.championId}
                  src={championData.icon}
                  alt={champion.championId}
                  variants={itemVariants}
                />
              );
            })}
          </motion.div>
        )}
      </div>
      <motion.button
        className="openInLabButton"
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleOpenInLab}
        title="Open this draft in the Lab"
      >
        Open in Lab
      </motion.button>
    </div>
  );
}

function GameHistory({ userId, onStatsLoaded }: GameHistoryProps) {
  const db = getFirestore();
  const [championsList, setChampionsList] = useState(new Map());

  useEffect(() => {
    const fetchAndSetChampions = async () => {
      const fetchedChampions = fetchChampions();
      setChampionsList(fetchedChampions);
    };

    fetchAndSetChampions();
  }, []);

  async function getLast10Games(userUid: string): Promise<Game[]> {
    const gamesRef = collection(db, "games");

    const last10GamesQuery = query(
      gamesRef,
      where("players", "array-contains", userUid),
      where("status", "==", "results"),
      orderBy("timestamp", "desc"),
      limit(10),
    );

    const querySnapshot = await getDocs(last10GamesQuery);
    const games: Game[] = [];

    querySnapshot.forEach((gameDoc) => {
      // eslint-disable-next-line fp/no-mutating-methods
      games.push({
        id: gameDoc.id,
        ...gameDoc.data(),
      } as Game);
    });

    return games;
  }

  const [games, setGames] = useState<Game[]>([]);

  useEffect(() => {
    async function fetchGames() {
      try {
        const userGames = await getLast10Games(userId);
        setGames(userGames);
        if (onStatsLoaded) {
          const wins = userGames.filter(
            (g) => g.winner?.userId === userId,
          ).length;
          onStatsLoaded({ wins, total: userGames.length });
        }
      } catch (error) {
        console.error("Error fetching games:", error);
      }
    }

    fetchGames();
  }, [userId]);

  return (
    <div className="GameHistory">
      <Suspense fallback={<div>Loading...</div>}>
        {games?.map((game, index) => (
          <GameComponent
            game={game}
            key={game.id || index}
            championsList={championsList}
            userUid={userId}
            fetchGameComponentFunction={(
              gameNeedFetch: Game,
              userUid: string,
            ) => fetchGameComponent(gameNeedFetch, userUid)}
          />
        ))}
      </Suspense>
    </div>
  );
}

export default GameHistory;
